
import { useState } from "react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { TabsList, TabsTrigger, Tabs } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Save, User, MapPin, Settings, Check } from "lucide-react";
import { ProfileFormPersonal } from "./ProfileFormPersonal";
import { ProfileFormAddress } from "./ProfileFormAddress";
import { toast } from "sonner";

interface ProfileFormData {
  nome: string;
  email: string;
  telefone: string;
  endereco: string;
  cidade: string;
  estado: string;
  cep: string;
  dataNascimento: string;
  bio: string;
  profilePicture?: string;
}

interface ProfileFormProps {
  formData: ProfileFormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  updateProfilePicture: (base64Image: string) => Promise<boolean>;
}

export const ProfileForm = ({ 
  formData, 
  handleChange, 
  handleSubmit, 
  isSubmitting,
  updateProfilePicture
}: ProfileFormProps) => {
  const [activeTab, setActiveTab] = useState("personal");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleImageUpload = async (file: File) => {
    // Verificar tamanho do arquivo (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 5MB");
      return;
    }

    // Verificar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast.error("O arquivo deve ser uma imagem");
      return;
    }

    // Converter para base64
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64Image = reader.result as string;
      
      try {
        const success = await updateProfilePicture(base64Image);
        if (success) {
          toast.success("Foto de perfil atualizada com sucesso");
        }
      } catch (error) {
        toast.error("Erro ao atualizar foto de perfil");
        console.error(error);
      }
    };
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await handleSubmit(e);
      setSaveSuccess(true);
      
      // Reset success state after animation
      setTimeout(() => {
        setSaveSuccess(false);
      }, 2000);
    } catch (error) {
      console.error("Error in form submission:", error);
    }
  };

  return (
    <Card className="shadow-sm border-gray-200 bg-white">
      <CardHeader className="pb-2">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 w-full bg-gray-100">
            <TabsTrigger 
              value="personal" 
              className={`flex items-center gap-2 ${activeTab === "personal" ? "bg-white text-primary" : "text-gray-600"}`}
            >
              <User className="h-4 w-4" />
              Dados Pessoais
            </TabsTrigger>
            <TabsTrigger 
              value="address" 
              className={`flex items-center gap-2 ${activeTab === "address" ? "bg-white text-primary" : "text-gray-600"}`}
            >
              <MapPin className="h-4 w-4" />
              Endereço
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              className={`flex items-center gap-2 ${activeTab === "settings" ? "bg-white text-primary" : "text-gray-600"}`}
            >
              <Settings className="h-4 w-4" />
              Configurações
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      
      <form onSubmit={onSubmit}>
        <CardContent className="pt-6">
          {activeTab === "personal" && (
            <ProfileFormPersonal 
              formData={formData} 
              handleChange={handleChange}
              handleImageUpload={handleImageUpload}
            />
          )}
          
          {activeTab === "address" && (
            <ProfileFormAddress 
              formData={formData} 
              handleChange={handleChange} 
            />
          )}
          
          {activeTab === "settings" && (
            <div className="space-y-6">
              <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
                <h3 className="text-sm font-medium text-yellow-800">Configurações da conta</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Esta seção permite configurar suas preferências de usuário e notificações.
                  Mais opções serão disponibilizadas em breve.
                </p>
              </div>
              
              <div className="border rounded-md p-4">
                <h3 className="text-sm font-medium mb-3">Preferências de Email</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input type="checkbox" id="email-updates" className="rounded text-primary mr-2" />
                    <label htmlFor="email-updates" className="text-sm">Receber atualizações por email</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" id="email-marketing" className="rounded text-primary mr-2" />
                    <label htmlFor="email-marketing" className="text-sm">Receber conteúdo de marketing</label>
                  </div>
                </div>
              </div>
              
              <div className="border rounded-md p-4">
                <h3 className="text-sm font-medium mb-3">Idioma e Região</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="text-sm block mb-1">Idioma</label>
                    <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
                      <option value="pt-BR">Português (Brasil)</option>
                      <option value="en-US">English (US)</option>
                      <option value="es">Español</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
        
        <CardFooter className="border-t px-6 py-4 flex justify-end bg-gray-50">
          <Button 
            type="submit" 
            disabled={isSubmitting} 
            className={`flex items-center gap-2 transition-all ${saveSuccess ? 'bg-green-600' : 'bg-energy-blue hover:bg-energy-blue-dark'}`}
          >
            {saveSuccess ? (
              <>
                <Check className="h-4 w-4" />
                Salvo com Sucesso
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {isSubmitting ? "Salvando..." : "Salvar Alterações"}
              </>
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};
